/**
 * Hero diagram: Files -> StampmatS -> three protection layers -> finalised record.
 *
 * Mobile-first: a vertical flow with down-arrows between stages, no fixed
 * widths, so it fits 320px. From xl up it becomes a four-column horizontal flow
 * with right-arrows. Pure HTML/CSS + inline SVG; no raster assets.
 */
import type { ReactNode } from "react";
import { Icon, type IconName } from "./icons";

const FILES: readonly { icon: IconName; label: string }[] = [
  { icon: "image", label: "Photo" },
  { icon: "video", label: "Video" },
  { icon: "file", label: "File" },
];

const LAYERS: readonly { icon: IconName; label: string; note: string }[] = [
  { icon: "lock", label: "Sealed", note: "against alteration" },
  { icon: "chain", label: "Chained", note: "changes become visible" },
  { icon: "clock", label: "Time-proven", note: "independently verifiable" },
];

export function HeroProofDiagram() {
  return (
    <figure
      className="rounded-xl border border-border bg-bg-soft p-4 sm:p-6"
      aria-label="How StampmatS turns files into a finalised evidence record"
    >
      <div className="grid grid-cols-1 items-center gap-2 xl:grid-cols-[minmax(0,0.9fr)_auto_minmax(0,1fr)_auto_minmax(0,1.1fr)_auto_minmax(0,1.3fr)] xl:gap-2.5">
        {/* Stage 1 — Files */}
        <Stage title="Your files">
          <ul className="flex justify-center gap-2 xl:flex-col xl:items-stretch" aria-label="Files you upload">
            {FILES.map((f) => (
              <li
                key={f.label}
                className="flex flex-1 items-center justify-center gap-2 rounded-md border border-border bg-white px-2 py-2 text-xs font-medium text-ink xl:justify-start xl:px-3"
              >
                <Icon name={f.icon} size={18} className="shrink-0 text-aigentico-navy" />
                {f.label}
              </li>
            ))}
          </ul>
        </Stage>

        <Connector />

        {/* Stage 2 — StampmatS */}
        <Stage title="StampmatS">
          <div className="flex items-center justify-center gap-2 rounded-md bg-aigentico-navy px-3 py-3 text-white">
            <Icon name="shield" size={20} className="shrink-0" />
            <span className="text-sm font-semibold tracking-wide">Finalise</span>
          </div>
          <p className="mt-2 text-center text-[11px] leading-snug text-ink-soft">
            SHA-256 fingerprint and deterministic manifest
          </p>
        </Stage>

        <Connector />

        {/* Stage 3 — Three layers */}
        <Stage title="Three layers">
          <ul className="grid grid-cols-3 gap-2 xl:grid-cols-1" aria-label="Protection layers">
            {LAYERS.map((l) => (
              <li
                key={l.label}
                className="flex flex-col items-center gap-1 rounded-md border border-aigentico-navy/30 bg-white px-1.5 py-2 text-center xl:flex-row xl:gap-2 xl:px-3 xl:text-left"
              >
                <Icon name={l.icon} size={18} className="shrink-0 text-aigentico-red" />
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-ink">{l.label}</span>
                  <span className="hidden text-[11px] leading-tight text-ink-soft sm:block xl:hidden">{l.note}</span>
                </span>
              </li>
            ))}
          </ul>
        </Stage>

        <Connector />

        {/* Stage 4 — Result */}
        <Stage title="Result">
          <div className="rounded-md border-2 border-aigentico-navy bg-white p-3 text-center xl:text-left">
            <div className="flex items-start justify-center gap-2 xl:justify-start">
              <Icon name="check" size={18} className="shrink-0 text-aigentico-navy" />
              <span className="text-sm font-semibold leading-snug text-aigentico-navy">Finalised evidence record</span>
            </div>
            <p className="mt-1 text-xs text-ink">File + manifest + proof</p>
            <p className="text-[11px] text-ink-soft">Ready to verify later</p>
          </div>
        </Stage>
      </div>
      <figcaption className="sr-only">
        Files are finalised by StampmatS, sealed against alteration, linked into an evidence chain, and
        anchored with an independently verifiable proof of time, producing a finalised evidence record made
        of the file, its manifest, and its proof.
      </figcaption>
    </figure>
  );
}

function Stage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-faint xl:text-left">
        {title}
      </div>
      {children}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex items-center justify-center text-ink-faint" aria-hidden="true">
      <Icon name="arrowDown" size={20} className="xl:hidden" />
      <Icon name="arrowRight" size={20} className="hidden xl:block" />
    </div>
  );
}
